/* Copyright 2016-2022
   Kabandy
   Author: Cedric Hoarau
 */

import { Network } from "./network";
import { UrlParam } from "../System";
import { ApiRouteFormatter, getAccessToken, getApiKey, getApiVersion, isCloudRunEnabled } from "../ApiWeb";
import { OAuthClientConf } from "./oauth-client-conf";

const ConnectResponseDivId = "ui-auth-connect-response-div";

let _rootDiv: HTMLDivElement | undefined = undefined;
let _onUserAuthenticated: Function | undefined = undefined;
let _accessToken: string | undefined = undefined;
let _revokeTokenButton: HTMLButtonElement | undefined = undefined;

const scope = "user-read-email workspace-read"; // optional
const enum EAuthFlow {
    Pkce = "PKCE",
    Simple = "Simple"
}
export const enum EAuthCacheKey {
    AuthState = "kab-auth-state",
    ClientId = "kab-client-id",
    ClientSecret = "kab-client-secret",
    CodeVerifier = "kab-code-verifier",
    RedirectUrl = "kab-redirect-url"
}

export namespace UIAuth {
    export function Bind(onUserAuthenticated: Function) {
        _onUserAuthenticated = onUserAuthenticated;
        
        document.body.appendChild( _InitializeUI() )
    }
    export function Unbind() {
        if (_rootDiv) {
            _rootDiv.remove();
        }

        if (_accessToken) {
            _accessToken = undefined;
        }
    }

    function _InitializeUI(): HTMLDivElement {
        let rootDiv = document.createElement("div");
        rootDiv.id = "ui-auth-root-div";

        let titleELement = document.createElement("h2");
        titleELement.innerText = "Authentication";
        rootDiv.appendChild(titleELement);

        // Access token workflow
        const accessToken: string = getAccessToken()

        // API Key workflow: user email + external company id
        const userEmail: string | null = UrlParam.Get(window.location.search, "userEmail");

        // OAuth2.0 Authorization Code Flow
        let oauth2FlowEnabled: boolean = false;

        // Access token
        if(accessToken) {
            new Promise(() => setTimeout( () => _HandleAuthResponse( { accessToken: accessToken, refreshToken: "", expiresIn: "" } ), 1000));
        }
        else if( userEmail ) {
            // @ts-ignore
            const sanitizedUserEmail: string = DOMPurify.sanitize(userEmail, { USE_PROFILES: { html: false } })
            const userEmailInput: HTMLInputElement = document.createElement("input");
            userEmailInput.readOnly = true;
            userEmailInput.style.backgroundColor = "#f0f0f0";
            userEmailInput.style.color = "#888";
            userEmailInput.id = "ui-auth-user-email-input";
            userEmailInput.value = sanitizedUserEmail;

            // label
            const emailLabel = document.createElement("label");
            emailLabel.textContent = "User email";
            emailLabel.setAttribute("for", userEmailInput.id);
            rootDiv.appendChild(emailLabel);
            rootDiv.appendChild(userEmailInput);

            const companyIdInput: HTMLInputElement = document.createElement("input");
            companyIdInput.readOnly = true;
            companyIdInput.style.backgroundColor = "#f0f0f0";
            companyIdInput.style.color = "#888";
            companyIdInput.id = "ui-auth-company-id-input";
            const companyId: string | null = UrlParam.Get(window.location.search, "companyId");
            if (companyId) {
                // @ts-ignore
                companyIdInput.value = DOMPurify.sanitize(companyId, { USE_PROFILES: { html: false } });
            }
            // label
            const companyIdLabel = document.createElement("label");
            companyIdLabel.textContent = "Company id";
            companyIdLabel.setAttribute("for", companyIdInput.id);
            rootDiv.appendChild(companyIdLabel);
            rootDiv.appendChild(companyIdInput);
            rootDiv.appendChild(document.createElement("br"));

            // button
            const connectFromEmailButton: HTMLButtonElement = document.createElement("button");
            connectFromEmailButton.id = "ui-auth-connect-from-email-id-button";
            connectFromEmailButton.textContent = "Connect from email and company external id";
            rootDiv.appendChild(connectFromEmailButton);
            connectFromEmailButton.addEventListener("click", () => {
                _AuthenticateFromUserIdAndCompanyId(userEmailInput.value, companyIdInput.value);
            });
            rootDiv.appendChild(document.createElement("br"));
        }
        // OAuth2.0 Authorization Code Flow
        else {
            oauth2FlowEnabled = true;

            // button
            const logInWithKabandyButton: HTMLButtonElement = document.createElement("button");
            logInWithKabandyButton.id = "ui-auth-login-with-kabandy-button";
            logInWithKabandyButton.textContent = "Log in with Kabandy";
            logInWithKabandyButton.addEventListener("click", async () => {
                // CSRF protection
                const authState : string = _GenerateAndStoreAuthState();

                // Authorization endpoint
                const cloudRunEnabled: boolean = isCloudRunEnabled();
                const authorizationEndpoint : string = ApiRouteFormatter.FormatAccountsAuthorizeUrl(cloudRunEnabled);

                // Client ID
                const clientId: string = _GetAndStoreClientId();

                // Client Secret
                const clientSecret: string = _GetAndStoreClientSecret();

                // Redirect URL <=> current url for the example
                const redirectUrl : string = _GetAndStoreRedirectUrl();

                // Without PKCE
                const authFlowStr : string = UrlParam.Get(window.location.search, "authFlow") || "";
                // @ts-ignore
                const sanitizedAuthFlowStr: string = DOMPurify.sanitize(authFlowStr, { USE_PROFILES: { html: false } })
                if( sanitizedAuthFlowStr === EAuthFlow.Simple ) {
                    _RedirectToKabAccountsAuthorize(authorizationEndpoint, { clientId: clientId, scope: scope, redirectUri: redirectUrl, state: authState, noCloudRun: !cloudRunEnabled });
                } 
                // With PKCE
                else {
                    const codeChallengeBase64 : string = await _GenerateAndStoreAuthCodeChallenge();
                    _RedirectToKabAccountsAuthorize(authorizationEndpoint, { clientId: clientId, scope: scope, redirectUri: redirectUrl, state: authState, codeChallengeBase64: codeChallengeBase64, noCloudRun: !cloudRunEnabled });
                }
            });
            rootDiv.appendChild(logInWithKabandyButton);
        }

        // connect response
        let connectResponse: HTMLDivElement = document.createElement("div");
        connectResponse.id = ConnectResponseDivId;
        rootDiv.appendChild(connectResponse);

        // revoke token button
        if( !oauth2FlowEnabled ) {
            _revokeTokenButton = document.createElement("button");
            _revokeTokenButton.id = "ui-auth-revoke-token-id-button";
            _revokeTokenButton.textContent = "Revoke token";
            _revokeTokenButton.disabled = true;
            rootDiv.appendChild(_revokeTokenButton);
            _revokeTokenButton.addEventListener("click", async () => {
                if (_accessToken) {
                    await _RevokeToken(_accessToken);
                    if (_revokeTokenButton) {
                        _revokeTokenButton.disabled = true;
                    }
                } else {
                    console.error("Invalid/Missing access token");
                }
            });
            rootDiv.appendChild(document.createElement("br"));
        }

        return rootDiv;
    }

    //
    // --- Authenticate

    async function _AuthenticateFromUserIdAndCompanyId(email: string, companyId: string): Promise<void> {
        const authApiUrl: string = ApiRouteFormatter.FormatAuthTokenUrl(isCloudRunEnabled(), getApiVersion());
        const response: { accessToken?: string; refreshToken?: string; expiresIn?: string, error?: any } = await Network.PostAuthToken(authApiUrl, getApiKey(), {
            email: email,
            companyId: companyId
        });
        _HandleAuthResponse(response);
    }
    function _HandleAuthResponse(response: { accessToken?: string; refreshToken?: string; expiresIn?: string, error?: any }): void {
        const connectResponseDiv: HTMLElement | null = document.getElementById(ConnectResponseDivId);
        if (connectResponseDiv) {
            if( response && response.accessToken ) {
                connectResponseDiv.textContent = "Access token - " + response.accessToken;
    
                if (_onUserAuthenticated) {
                    _onUserAuthenticated(response.accessToken);
                }

                _accessToken = response.accessToken;

                if (_revokeTokenButton) {
                    _revokeTokenButton.disabled = false;
                }
            }
            else {
                let innerHtmlErr: string = "Authentication has failed";

                if( response && response.error ) {
                    if( response.error.code ) {
                        innerHtmlErr += ` - ${response.error.code}`;
                    }
                    if( response.error.message ) {
                        innerHtmlErr += ` - ${response.error.message}`;
                    }
                }   

                connectResponseDiv.textContent = innerHtmlErr;
            }
        }
    }

    //
    // --- Revoke token

    async function _RevokeToken(accessToken: string): Promise<void> {
        const authRevokeTokenApiUrl: string = ApiRouteFormatter.FormatAuthTokenRevokeUrl(isCloudRunEnabled(), getApiVersion());
        await Network.PostAuthTokenRevoke(authRevokeTokenApiUrl, getApiKey(), accessToken);
    }

    //
    // --- Redirect to https://accounts.kabandy.com/authorize

    function _RedirectToKabAccountsAuthorize( url: string, options: { clientId: string, scope: string, redirectUri: string, state: string, codeChallengeBase64?: string, noCloudRun?: boolean }) {
        let authUrl = new URL(url)
        let params : any = {
            response_type: "code",
            state: options.state,
            client_id: options.clientId,
            scope: options.scope,
            redirect_uri: options.redirectUri,
        };

        if( options.codeChallengeBase64 && options.codeChallengeBase64 !== "" ) {
            params["code_challenge_method"] = "S256";
            params["code_challenge"] = options.codeChallengeBase64;
        }

        if( options.noCloudRun === true ) {
            params.noCloudRun = "1";
        }

        authUrl.search = new URLSearchParams(params).toString();

        window.location.href = authUrl.toString();
    }

    //
    // --- OAuth 2.0 Authorization Code Flow

    function _FormatUint8ArrayToHexString(uint8Array: Uint8Array) : string {
        let hexString = '';
        for (let i = 0; i < uint8Array.length; i++) {
          const hex = uint8Array[i].toString(16).padStart(2, '0');
          hexString += hex;
        }
        return hexString;
    }
    function _GenerateRandomString(length : number ) : string {
        const randomValues = new Uint8Array(length);
        crypto.getRandomValues(randomValues);
        const hexStr : string = _FormatUint8ArrayToHexString( randomValues );
        return hexStr.slice(0, length);
    }
    function _GenerateAndStoreAuthState() : string {
        const state : string = _GenerateRandomString(16);
        localStorage.setItem(EAuthCacheKey.AuthState, state);
        return state;
    }
    async function _GenerateAndStoreAuthCodeChallenge(): Promise<string> {
        const possible : string = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        const randomValues : Uint8Array  = crypto.getRandomValues(new Uint8Array(64));
        const randomString : string = randomValues.reduce((acc, x) => acc + possible[x % possible.length], "");
        
        // Store the code verifier
        const codeVerifier : string = randomString;
        window.localStorage.setItem( EAuthCacheKey.CodeVerifier, codeVerifier);

        // Encrypt the code "challenge" 
        const data : Uint8Array = new TextEncoder().encode(codeVerifier);
        const hashed : ArrayBuffer = await crypto.subtle.digest('SHA-256', data);
        
        // Format in base64 string
        const codeChallengeBase64 : string = btoa(String.fromCharCode(...new Uint8Array(hashed)))
            .replace(/=/g, '')
            .replace(/\+/g, '-')
            .replace(/\//g, '_');

        return codeChallengeBase64;
    }
    function _GetAndStoreClientId() : string {
        const clientId: string = OAuthClientConf.ClientId();
        if( clientId ) {
            window.localStorage.setItem( EAuthCacheKey.ClientId, clientId);
        }
        return clientId;
    }
    function _GetAndStoreClientSecret() : string {
        const clientSecret: string = OAuthClientConf.ClientSecret();
        if( clientSecret ) {
            window.localStorage.setItem( EAuthCacheKey.ClientSecret, clientSecret);
        }
        return clientSecret;
    }
    function _GetAndStoreRedirectUrl() : string {
        const redirectUrl : string = new URL(window.location.href).origin;
        if( redirectUrl ) {
            window.localStorage.setItem( EAuthCacheKey.RedirectUrl, redirectUrl);
        }
        return redirectUrl;
    }
}
